import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2,
  Printer,
  Phone,
  Mail,
  MapPin,
  Clock,
  Instagram,
  Facebook,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- Constants & fallbacks ----------
const FALLBACK_BY_CATEGORY = {
  combos: "/combos/btw-super-combo.jpg",
  burgers: "/combos/btw-slider-combo.jpg",
  "taco-bar": "/combos/btw-3-tacos-combo.jpg",
  wings: "/catering/btw-20-wing-sampler.jpg",
  seafood: "/combos/btw-5-fries.jpg",
  "sides-soups-salads": "/combos/btw-5-fries.jpg",
  drinks: "/hero/btw-spread.jpg",
  dessert: "/catering/carrot-cake-14-slices-8b3dea42.jpg",
  "family-meals": "/combos/btw-super-combo.jpg",
  specials: "/combos/btw-3-tacos-combo.jpg",
};
const GLOBAL_FALLBACK = "/hero/btw-spread.jpg";

const resolveImg = (item, catId) => {
  const raw = (item?.img || "").trim();
  return raw || FALLBACK_BY_CATEGORY[catId] || GLOBAL_FALLBACK;
};

// TikTok isn't in lucide-react; tiny inline SVG.
function TikTokIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.7a8.16 8.16 0 0 0 4.77 1.52V6.84a4.85 4.85 0 0 1-1.84-.15z" />
    </svg>
  );
}

export default function BifoldMenu() {
  const [menu, setMenu] = useState(null);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFoldLines, setShowFoldLines] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [m, s] = await Promise.all([
          axios.get(`${API}/menu`),
          axios.get(`${API}/site/content`).catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setMenu(m.data);
        setSite(s.data || {});
      } catch (e) {
        console.error("[bifold] load failed", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-200">
        <Loader2 className="w-7 h-7 animate-spin text-black/60" />
      </div>
    );
  }
  if (!menu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-black">Menu unavailable.</p>
      </div>
    );
  }

  // Real contact info — pulled from /api/site/content (admin-editable) with
  // sane defaults so the bi-fold always renders even pre-seed.
  const f = site?.footer || {};
  const addr1 = f.address_line1 || "110-46 Merrick Blvd";
  const addr2 = f.address_line2 || "Jamaica, New York 11433";
  const phone = f.phone || "(718) 297-4BTW (4289)";
  const email = f.email || "info@btwnyc.com";
  const igHandle = f.ig_handle || "@burgerstacoswings";
  const fbHandle = "facebook.com/burgerstacoswings";
  const tikHandle = f.tiktok_handle || "@btwnyc";
  const hours = (f.hours && f.hours.length ? f.hours : null) || [
    { days: "Monday", time: "Closed" },
    { days: "Tue – Thu", time: "12:00 PM – 8:00 PM" },
    { days: "Fri – Sat", time: "12:00 PM – 10:00 PM" },
    { days: "Sunday", time: "12:00 PM – 8:00 PM" },
  ];

  // Distribute categories across 4 vertical columns for the inside spread.
  // Simple round-robin by total item count keeps columns roughly balanced.
  const cats = menu.categories;
  const columns = [[], [], [], []];
  const totals = [0, 0, 0, 0];
  for (const c of cats) {
    const idx = totals.indexOf(Math.min(...totals));
    columns[idx].push(c);
    totals[idx] += c.items.length + 1; // +1 for header weight
  }

  return (
    <div className="bifold-root bg-neutral-300 min-h-screen" data-testid="bifold-menu-page">
      {/* Floating toolbar — hidden on print */}
      <div className="no-print sticky top-0 z-30 bg-black text-white border-b border-white/10">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-8 py-3 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <span className="font-black tracking-tight text-lg">BTW Bi-Fold Menu</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#5FBDD6] font-bold hidden sm:inline">
              11×17 · prints as 2 sheets · fold along center line
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showFoldLines}
                onChange={(e) => setShowFoldLines(e.target.checked)}
                className="accent-[#5FBDD6]"
                data-testid="bifold-toggle-fold-lines"
              />
              Show fold lines
            </label>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="bifold-print-btn"
              className="px-4 py-2 text-sm font-bold uppercase tracking-wider bg-[#A3D900] text-black hover:brightness-95 transition inline-flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Print Bi-Fold
            </button>
          </div>
        </div>
      </div>

      {/* PAGE 1 — OUTSIDE SPREAD (back cover | front cover) */}
      <article className={`bifold-sheet ${showFoldLines ? "show-fold" : ""}`} data-testid="bifold-outside">
        {/* BACK COVER — left panel of the outside */}
        <section className="panel back-cover bg-white text-[#0B0B0B] relative overflow-hidden">
          <div className="relative h-full flex flex-col p-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#5FBDD6] font-black">Find us</div>
              <h2 className="font-black text-5xl mt-2 leading-[0.95] tracking-tight">
                Come<br/>through.
              </h2>
              <div className="mt-3 w-14 h-1 bg-[#5FBDD6]" />
            </div>

            <div className="mt-10 space-y-7">
              <div className="flex items-start gap-4">
                <MapPin className="w-7 h-7 text-[#5FBDD6] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-black/50 font-bold mb-0.5">Address</div>
                  <div className="font-extrabold text-xl leading-tight">{addr1}</div>
                  <div className="text-black/70 text-base">{addr2}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="w-7 h-7 text-[#5FBDD6] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-black/50 font-bold mb-0.5">Call</div>
                  <div className="font-extrabold text-xl">{phone}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-7 h-7 text-[#5FBDD6] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-black/50 font-bold mb-0.5">Email</div>
                  <div className="font-extrabold text-xl">{email}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="w-7 h-7 text-[#5FBDD6] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-black/50 font-bold mb-1">Hours</div>
                  <table className="text-base">
                    <tbody>
                      {hours.map((h) => (
                        <tr key={h.days}>
                          <td className="pr-5 font-extrabold whitespace-nowrap">{h.days}</td>
                          <td className="text-black/70">{h.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-8 border-t-2 border-black/20">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#5FBDD6] font-black mb-4">Follow the flavor</div>
              <div className="space-y-2.5 text-base">
                <div className="flex items-center gap-3"><Instagram className="w-5 h-5 text-[#5FBDD6]" /> <span className="font-extrabold">{igHandle}</span></div>
                <div className="flex items-center gap-3"><Facebook className="w-5 h-5 text-[#5FBDD6]" /> <span className="font-extrabold">{fbHandle}</span></div>
                <div className="flex items-center gap-3"><TikTokIcon className="w-5 h-5 text-[#5FBDD6]" /> <span className="font-extrabold">{tikHandle}</span></div>
              </div>
              <div className="mt-5 text-[10px] uppercase tracking-[0.4em] text-black/45">
                Order online · btwnyc.com
              </div>
            </div>
          </div>
        </section>

        {/* FRONT COVER — right panel of the outside */}
        <section className="panel front-cover bg-white text-[#0B0B0B] relative overflow-hidden">
          <div className="relative h-full flex flex-col items-center justify-between p-12 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.45em] text-[#5FBDD6] font-black">Jamaica, Queens</div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.45em] text-black/45 font-bold">Est. 2024</div>
              <div className="mt-3 mx-auto w-14 h-1 bg-[#5FBDD6]" />
            </div>

            <div className="flex-1 w-full flex flex-col items-center justify-center">
              <img
                src="/brand/btw-logo.png"
                alt="BTW — Burgers · Tacos · Wings"
                className="w-[78%] max-w-[460px] h-auto"
                onError={(e) => { e.currentTarget.src = "/brand/btw-logo-centered.png"; }}
              />
              <img
                src="/brand/btw-mascot.png"
                alt="BTW Mascot"
                className="mt-6 w-[42%] max-w-[230px] h-auto"
              />
            </div>

            <div className="w-full">
              <div className="text-[11px] tracking-[0.6em] uppercase font-black text-[#0B0B0B]">
                Burgers · Tacos · Wings
              </div>
              <div className="mt-6 inline-block px-5 py-2 border-2 border-[#0B0B0B]">
                <div className="text-[10px] uppercase tracking-[0.35em] font-extrabold text-[#0B0B0B]">Menu</div>
              </div>
              <div className="mt-6 text-xs text-black/55 italic">
                "Bold flavor. Made fresh. Built for you."
              </div>
            </div>
          </div>
        </section>
      </article>

      {/* PAGE 2 — INSIDE SPREAD (full menu, 4 columns) */}
      <article className={`bifold-sheet ${showFoldLines ? "show-fold" : ""}`} data-testid="bifold-inside">
        <section className="panel inside-left bg-white text-black relative overflow-hidden">
          <div className="h-full flex flex-col p-8">
            <header className="flex items-center justify-between pb-4 border-b-[3px] border-black">
              <div>
                <div className="text-[9px] uppercase tracking-[0.35em] text-[#5FBDD6] font-black">The full menu</div>
                <div className="font-black text-3xl tracking-tight">BTW Eats</div>
              </div>
              <img src="/brand/btw-badge.png" alt="" className="h-14 w-14 object-contain" />
            </header>
            <div className="mt-6 flex-1 grid grid-cols-2 gap-x-7">
              {columns.slice(0, 2).map((cats, ci) => (
                <BifoldColumn key={ci} cats={cats} />
              ))}
            </div>
          </div>
        </section>

        <section className="panel inside-right bg-white text-black relative overflow-hidden">
          <div className="h-full flex flex-col p-8">
            <header className="flex items-center justify-between pb-4 border-b-[3px] border-black">
              <img src="/brand/btw-mascot.png" alt="" className="h-14 w-auto" />
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.35em] text-[#5FBDD6] font-black">Order online</div>
                <div className="font-black text-3xl tracking-tight">btwnyc.com</div>
              </div>
            </header>
            <div className="mt-6 flex-1 grid grid-cols-2 gap-x-7">
              {columns.slice(2, 4).map((cats, ci) => (
                <BifoldColumn key={ci} cats={cats} />
              ))}
            </div>
            <footer className="mt-5 pt-3 border-t border-black/15 text-[9px] text-black/55 text-center">
              Prices and availability subject to change · Closed Mondays · {phone}
            </footer>
          </div>
        </section>
      </article>

      <style>{`
        .bifold-root { font-family: 'Inter', system-ui, -apple-system, sans-serif; }

        /* Each printed sheet = 17" wide x 11" tall when laid flat */
        .bifold-sheet {
          width: 17in;
          height: 11in;
          margin: 28px auto;
          background: #fff;
          display: flex;
          box-shadow: 0 18px 60px rgba(0,0,0,0.25);
          position: relative;
        }
        .bifold-sheet .panel {
          flex: 1 1 50%;
          width: 8.5in;
          height: 11in;
          position: relative;
        }
        .bifold-sheet.show-fold::after {
          content: '';
          position: absolute;
          top: 0; bottom: 0; left: 50%;
          border-left: 1px dashed rgba(0,0,0,0.55);
          pointer-events: none;
        }

        /* --- inside menu column styling --- */
        .bf-cat-label {
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #0B0B0B;
          border-bottom: 2px solid #0B0B0B;
          padding-bottom: 3px;
          margin-bottom: 5px;
        }
        .bf-item {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 3px 0;
          break-inside: avoid;
        }
        .bf-thumb {
          flex: 0 0 38px;
          width: 38px; height: 38px;
          overflow: hidden;
          background: #f4f4f4;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 4px;
        }
        .bf-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .bf-name {
          font-size: 10.5px;
          font-weight: 700;
          color: #0B0B0B;
          line-height: 1.15;
          flex: 1 1 auto;
        }
        .bf-price {
          font-size: 10.5px;
          font-weight: 900;
          color: #0B0B0B;
          white-space: nowrap;
        }

        @media print {
          @page { size: 17in 11in; margin: 0; }
          .no-print { display: none !important; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .bifold-root { background: #fff !important; min-height: 0 !important; }
          .bifold-sheet {
            margin: 0 !important;
            box-shadow: none !important;
            page-break-after: always;
            page-break-inside: avoid;
          }
          .bifold-sheet:last-of-type { page-break-after: auto; }
          /* Hide the on-screen fold guide in print — pro printers add it. */
          .bifold-sheet.show-fold::after { display: none; }
        }
      `}</style>
    </div>
  );
}

function BifoldColumn({ cats }) {
  return (
    <div className="flex flex-col gap-5">
      {cats.map((cat) => (
        <section key={cat.id} className="break-inside-avoid">
          <h3 className="bf-cat-label">{cat.label}</h3>
          <div>
            {cat.items.map((item, idx) => (
              <div key={`${cat.id}-${idx}`} className="bf-item">
                <div className="bf-thumb">
                  <img
                    src={resolveImg(item, cat.id)}
                    alt={item.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_BY_CATEGORY[cat.id] || GLOBAL_FALLBACK;
                    }}
                  />
                </div>
                <div className="bf-name">{item.name}</div>
                <div className="bf-price">{item.price}</div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
